import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import type {SxProps} from '@mui/system';
import type {Dayjs} from 'dayjs';
import dayjs from 'dayjs';
import {type FC, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import AutocompleteSelect from '@/components/common/forms/AutocompleteSelect';
import CalendarPicker from '@/components/common/forms/CalendarPicker';
import InputText from '@/components/common/forms/InputText';
import Select from '@/components/common/forms/Select';
import Label from '@/components/common/Label';
import Title from '@/components/common/Title';
import SpeciesSelector from '@/components/ForestMassUploadForm/SpeciesSelector';
import UploadFileButton from '@/components/ForestMassUploadForm/UploadFileButton';
import {COUNCILS} from '@/config';
import type {Appraiser} from '@/domain/entities/Appraiser';
import type {UploadForestMass} from '@/domain/entities/Op';
import type {Species} from '@/domain/entities/Species';

//TYPES
type Props = {
  appraisers?: Array<Appraiser>;
  species?: Array<Species>;
  onUpload: (forestMass: UploadForestMass, file: File) => Promise<void>;
  onBack: () => void;
};

const ForestMassUploadForm: FC<Props> = ({appraisers, species, onUpload, onBack}) => {
  const {t} = useTranslation();

  const initialForestMass = {plannedCuttingDate: new Date()};
  const [partialEntity, setPartialEntity] = useState<Partial<UploadForestMass>>(initialForestMass);
  const [file, setFile] = useState<File>();

  const handleConcello = (concello: string) => setPartialEntity({...partialEntity, concello});
  const handleCode = (code: string) => setPartialEntity({...partialEntity, code});
  const handleAppraiser = (optionId?: string) => setPartialEntity({...partialEntity, appraiserUuid: optionId});
  const handleDate = (newDate?: Dayjs) =>
    newDate && setPartialEntity({...partialEntity, plannedCuttingDate: newDate.toDate()});
  const handleSpecies = (species: string[]) => setPartialEntity({...partialEntity, speciesUuids: species});

  const selectedConcello = useMemo(
    () => COUNCILS.find((council) => council === partialEntity.concello),
    [partialEntity],
  );

  const entity = useMemo(() => {
    const {code, plannedCuttingDate, appraiserUuid, speciesUuids, concello} = partialEntity;
    if (!code || !plannedCuttingDate || !speciesUuids || !speciesUuids.length || !concello) return;
    const entity: UploadForestMass = {
      code,
      plannedCuttingDate,
      appraiserUuid,
      speciesUuids,
      concello,
    };
    return entity;
  }, [partialEntity]);

  const isUploadEnabled = entity && file;

  const handleUpload = () => {
    if (entity && file) {
      onUpload(entity, file).then(() => onBack());
    }
  };

  return (
    <Stack direction="column" sx={styles.container}>
      <Title onBack={onBack} label={t('forestMass.uploadTitle')} />
      <Label value={t('forestMass.concello')} required={true}>
        <AutocompleteSelect options={COUNCILS} value={selectedConcello} onValueChange={handleConcello} />
      </Label>
      <Label value={t('forestMass.geometry')} required={true}>
        <UploadFileButton
          placeholder={t('forestMass.geopackage')}
          file={file}
          onFileSelected={setFile}
          acceptMimeType={'application/geopackage+sqlite3'}
        />
      </Label>
      <Label value={t('forestMass.code')} required={true}>
        <InputText value={partialEntity?.code} onChange={handleCode} />
      </Label>
      <Label value={t('forestMass.appraiser')}>
        <Select
          options={appraisers ? appraisers : []}
          selectedOptionId={partialEntity?.appraiserUuid}
          onChange={handleAppraiser}
        />
      </Label>
      <Label value={t('forestMass.species')} required={true}>
        <SpeciesSelector species={species ? species : []} onSelectionChange={handleSpecies} />
      </Label>
      <Label value={t('forestMass.plannedCuttingDate')} required={true}>
        <CalendarPicker
          value={partialEntity ? dayjs(partialEntity.plannedCuttingDate) : dayjs(new Date())}
          onChange={handleDate}
        />
      </Label>

      <Button variant="contained" onClick={handleUpload} sx={styles.button} disabled={!isUploadEnabled}>
        {t('forestMass.upload')}
      </Button>
    </Stack>
  );
};
export default ForestMassUploadForm;

const styles: Record<string, SxProps> = {
  titleContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: 1,
    mb: '1rem',
  },
  container: {
    margin: '1rem',
    width: {xs: '90vw', sm: '70vw', md: '35vw'},
  },
  title: {
    color: 'primary.main',
    backgroundColor: 'rgba(0,0,0,0)',
  },
  button: {
    mt: '2rem',
  },
  uploadGeometryButton: {
    mb: '0.75rem',
    color: 'primary.main',
    backgroundColor: 'common.white',
    borderRadius: '0%',
  },
};
